import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../../i18n';
import { api } from '../../../lib/api';
import { displayManageSearchPath } from '../../displays/manage/displayManageRoutes';
import { invalidateAfterPlayContent } from '../../displays/play-content/invalidateAfterPlayContent';
import { defaultPlaylistDurationSeconds } from '../buildContentLibrary';
import { UPLOAD_FOR_SCREEN_PARAM } from '../contentRoutes';
import { isMediaUsableInPlaylist } from '../mediaOperatorStatus';

export type ScreenUploadPhase = 'idle' | 'waiting' | 'playing' | 'error';

export type ScreenUploadIntent = {
  screenId: string;
  screenName: string | null;
  phase: ScreenUploadPhase;
  error: string | null;
  /** Queue freshly uploaded assets to play on the display once ready. */
  track: (assetIds: string[]) => void;
  cancel: () => void;
};

/**
 * A Library visit that started from a display's "Add content". Uploads made
 * here go onto that display as soon as they're ready, in upload order, and the
 * operator is returned to the display. Null when the visit has no display.
 */
export function useScreenUploadIntent(
  organisationId: string,
  assets: MediaAsset[],
): ScreenUploadIntent | null {
  const { t } = useTranslation('content');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const screenId = searchParams.get(UPLOAD_FOR_SCREEN_PARAM);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Effects re-run on unrelated renders; never start the same play twice.
  const playingIdRef = useRef<string | null>(null);

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    enabled: Boolean(screenId),
  });
  const screenName = screensQuery.data?.find((item) => item.id === screenId)?.name ?? null;

  useEffect(() => {
    setPendingIds([]);
    setStarted(false);
    setError(null);
  }, [screenId]);

  const play = useMutation({
    mutationFn: (asset: MediaAsset) =>
      api.playContentOnScreen(organisationId, screenId!, {
        mediaAssetId: asset.id,
        durationSeconds: defaultPlaylistDurationSeconds(asset),
      }),
    onError: (err) => setError(apiErrorMessage(err, t('forScreen.playError'))),
    onSettled: (_data, _err, asset) => {
      playingIdRef.current = null;
      setPendingIds((ids) => ids.filter((id) => id !== asset.id));
    },
  });

  const nextAsset = pendingIds.length > 0 ? assets.find((item) => item.id === pendingIds[0]) : undefined;
  const nextReady = nextAsset ? isMediaUsableInPlaylist(nextAsset) : false;

  useEffect(() => {
    if (!screenId || !nextAsset || playingIdRef.current) return;
    if (nextAsset.status === 'failed') {
      setPendingIds((ids) => ids.filter((id) => id !== nextAsset.id));
      setError(t('forScreen.failed', { name: nextAsset.originalFilename }));
      return;
    }
    if (!nextReady) return;
    playingIdRef.current = nextAsset.id;
    play.mutate(nextAsset);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the head of the queue changes state
  }, [screenId, nextAsset?.id, nextAsset?.status, nextReady]);

  useEffect(() => {
    if (!screenId || !started || pendingIds.length > 0 || play.isPending || error) return;
    invalidateAfterPlayContent(queryClient, organisationId, screenId);
    navigate(displayManageSearchPath(screenId));
  }, [screenId, started, pendingIds.length, play.isPending, error, queryClient, organisationId, navigate]);

  if (!screenId) return null;

  return {
    screenId,
    screenName,
    phase: error ? 'error' : play.isPending ? 'playing' : pendingIds.length > 0 ? 'waiting' : 'idle',
    error,
    track: (assetIds) => {
      if (assetIds.length === 0) return;
      setError(null);
      setStarted(true);
      setPendingIds((ids) => [...ids, ...assetIds]);
    },
    cancel: () => {
      setPendingIds([]);
      setStarted(false);
      const next = new URLSearchParams(searchParams);
      next.delete(UPLOAD_FOR_SCREEN_PARAM);
      setSearchParams(next, { replace: true });
    },
  };
}
